import angular, { IHttpResponse } from 'angular';
import { Services } from '../@types/services';
import {
  FormFieldType,
  LookupSpecialFieldLabels,
  TableCellType
} from './flowingly.constants';
import { LookupSpecialFields } from '@Shared.Angular/@types/core/contracts/queryModel/workflow/lookupSpecialFields';
import IFormField from '@Shared.Angular/@types/core/contracts/queryModel/card/formField';
import ICustomTableName from '@Shared.Angular/@types/customDb/services/models/customTableName';
import ICustomDatabaseColumn from '@Shared.Angular/@types/customDb/services/models/customDatabaseColumn';
import { ILookupValueOption } from '@Shared.Angular/@types/lookupValueOption';
import {
  IFieldOptionQuery,
  IFieldOptionResult
} from '@Shared.Angular/@types/fieldOptions';

angular
  .module('flowingly.services')
  .factory('customdbService', customdbService);

customdbService.$inject = ['APP_CONFIG', '$http', '$q'];

function customdbService(
  APP_CONFIG: Services.APP_CONFIG,
  $http: angular.IHttpService,
  $q: angular.IQService
) {
  const databasesApiBaseUrl = APP_CONFIG.apiBaseUrl + 'customdatabase';
  let cachedDatabases: ICustomTableName[] | null = null;
  let databasesPromise: ng.IPromise<ICustomTableName[]> | null = null;
  //API
  const service = {
    populateValueOptions: populateValueOptions,
    getColumnsUnfiltered,
    getColumns,
    getAllDatabases,
    getDatabaseRecords,
    getFieldOptions,
    getSchemaDataTypes,
    searchDatabaseRecords,
    databasesApiBaseUrl
  };

  return service;

  //////////// Helper Methods

  //////////// Public API Methods
  function getAllDatabases() {
    if (cachedDatabases) {
      return $q.resolve(cachedDatabases);
    }

    if (databasesPromise) {
      return databasesPromise;
    }

    databasesPromise = $http
      .get(databasesApiBaseUrl + '?fromModeler=true', { noSpinner: true })
      .then((response: IHttpResponse<ICustomTableName[]>) => {
        cachedDatabases = response.data;
        return cachedDatabases;
      })
      .catch((err) => {
        databasesPromise = null;
        return $q.reject(err);
      });

    return databasesPromise;
  }

  function getColumns(
    dbName: string,
    _config_: angular.IRequestShortcutConfig = {}
  ): ng.IPromise<ICustomDatabaseColumn[]> {
    return getColumnsUnfiltered(dbName, _config_).then((columns) => {
      const idColumnIndex = columns.findIndex((d) => d.name === 'Id');
      const customIdColumnIndex = columns.findIndex(
        (d) => d.name === 'customdatabaseid'
      );

      if (idColumnIndex >= 0 && customIdColumnIndex < 0) {
        columns.splice(idColumnIndex, 1);
      } else if (customIdColumnIndex >= 0) {
        columns.splice(customIdColumnIndex, 1);
      }
      return columns;
    });
  }

  function getColumnsUnfiltered(
    physicalTableName: string,
    _config_: angular.IRequestShortcutConfig = {}
  ): ng.IPromise<ICustomDatabaseColumn[]> {
    const config = {
      noSpinner: true,
      ..._config_
    };

    // Resolve to physical table name before making API call
    return $http
      .get<ICustomDatabaseColumn[]>(
        `${databasesApiBaseUrl}/columns/${physicalTableName}`,
        config
      )
      .then((response) => response.data);
  }

  function getDatabaseRecords(physicalTableName: string, _config_ = {}) {
    const config = {
      noSpinner: false,
      ..._config_
    };

    return $http
      .get(`${databasesApiBaseUrl}/${physicalTableName}`, config)
      .then((response) => response.data);
  }

  function getFieldOptions(request: IFieldOptionQuery[], _config_ = {}) {
    const config = {
      noSpinner: false,
      ..._config_
    };

    return $http
      .post<IFieldOptionResult[]>(
        `${databasesApiBaseUrl}/fields/options`,
        request,
        config
      )
      .then((response) => response.data);
  }

  function getSchemaDataTypes(_config_ = {}) {
    const config = {
      noSpinner: false,
      ..._config_
    };

    return $http
      .get(`${databasesApiBaseUrl}/schema/datatypes`, config)
      .then((response) => response.data);
  }

  function searchDatabaseRecords(
    physicalTableName: string,
    fieldName: string,
    searchValue: string,
    allDatabases?: ICustomTableName[],
    _config_ = {}
  ) {
    const config = {
      noSpinner: true,
      ..._config_
    };

    return $http
      .get(
        `${databasesApiBaseUrl}/${physicalTableName}/${fieldName}/${searchValue}`,
        config
      )
      .then((response) => response.data);
  }

  function populateValueOptions(
    data: { allFields: IFormField[]; cells?: any[] },
    field: IFormField,
    dataType,
    addCurrentActorOptions = false
  ): ILookupValueOption[] {
    let valueOptions: ILookupValueOption[] = [];
    const isTable = data.cells !== undefined;

    valueOptions.push({
      name: LookupSpecialFields.InitiatorFullName,
      text: LookupSpecialFieldLabels.InitiatorFullName,
      type: 'text'
    });

    valueOptions.push({
      name: LookupSpecialFields.InitiatorEmail,
      text: LookupSpecialFieldLabels.InitiatorEmail,
      type: 'text'
    });

    if (addCurrentActorOptions) {
      valueOptions.push({
        name: LookupSpecialFields.CurrentActorFullName,
        text: LookupSpecialFieldLabels.CurrentActorFullNameText,
        type: 'text'
      });
      valueOptions.push({
        name: LookupSpecialFields.CurrentActorEmail,
        text: LookupSpecialFieldLabels.CurrentActorEmailText,
        type: 'text'
      });
    }

    if (data.allFields || data.cells) {
      if (isTable) {
        for (const c of data.cells) {
          if (c.id === field.id) {
            break;
          } else if (
            c.type === TableCellType.TEXT ||
            c.type === TableCellType.CURRENCY ||
            c.type === TableCellType.NUMBER ||
            c.type === TableCellType.DROPDOWN
          ) {
            valueOptions.push({
              name: c.id + '', //kendo don't like int by some reason
              text: c.header,
              type: c.type
            });
          }
        }
      } else {
        for (const f of data.allFields) {
          if (f.name === field.name) {
            break;
          } else if (
            f.type === FormFieldType.TEXT ||
            f.type === FormFieldType.NUMBER ||
            f.type === FormFieldType.SELECT_LIST ||
            f.type === FormFieldType.RADIO_BUTTON_LIST ||
            f.type === FormFieldType.CURRENCY ||
            f.type === FormFieldType.EMAIL ||
            f.type === FormFieldType.FORMULA ||
            (f.type === FormFieldType.LOOKUP && f.lookupConfig)
          ) {
            valueOptions.push({
              name: f.name,
              text: f.displayName,
              type: f.type
            });
          }
        }
      }

      // ensure field type matches data type, eg. validate and prevent short text field with number data column
      if (dataType && dataType !== 'text') {
        valueOptions = valueOptions.filter((o) => o.type === dataType);
      }
    }

    return valueOptions;
  }
}

export type CustomdbServiceType = ReturnType<typeof customdbService>;
